import { useState, useEffect, useMemo } from 'react'
import { PortalLayout } from '../components/PortalLayout'
import { api } from '../api'

const TABS = ['dashboard', 'jobs', 'providers'] as const
type Tab = typeof TABS[number]

interface WorkshopJob {
  id: number
  vehicle_id: number
  vehicle_reg: string
  job_type: string
  priority: 'vor' | 'safety_critical' | 'urgent' | 'routine'
  title: string
  workshop_provider_id?: number
  workshop_provider_name?: string
  scheduled_date?: string
  assigned_to_name?: string
  status: 'requested' | 'scheduled' | 'in_progress' | 'awaiting_parts' | 'completed'
  parts?: { id: number; part_number?: string; description: string; quantity: number; unit_cost?: number; total_cost?: number }[]
  history?: { id: number; old_status?: string; new_status: string; changed_by?: string; notes?: string; created_at?: string }[]
  notes?: string
  description?: string
  estimated_cost?: number
  actual_cost?: number
}

interface WorkshopProvider {
  id: number
  name: string
  contact_name?: string
  phone?: string
  email?: string
  specialisations: string[]
  hourly_rate: number
}

interface DashboardData {
  vor_count: number
  active_jobs: number
  month_cost: number
  completed_this_month: number
  by_status?: Record<string, number>
  total_estimated_cost?: number
}

const priorityConfig = {
  vor: { bg: 'bg-red-100', text: 'text-red-800', label: 'VOR' },
  safety_critical: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Safety Critical' },
  urgent: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Urgent' },
  routine: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Routine' },
}

const statusConfig = {
  requested: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Requested' },
  scheduled: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Scheduled' },
  in_progress: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'In Progress' },
  awaiting_parts: { bg: 'bg-red-100', text: 'text-red-800', label: 'Awaiting Parts' },
  completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Completed' },
}

function PriorityBadge({ priority }: { priority: string }) {
  const config = priorityConfig[priority as keyof typeof priorityConfig]
  if (!config) return null
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  )
}

function StatusBadge({ status }: { status: string }) {
  const config = statusConfig[status as keyof typeof statusConfig]
  if (!config) return null
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  )
}

function DashboardTab() {
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const result = await api.getWorkshopDashboard()
        setData(result)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load dashboard')
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-sm text-gray-500">Loading dashboard...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-sm text-red-800">
        {error}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">VOR Count</div>
          <div className="mt-2 text-3xl font-bold text-red-600">{data?.vor_count || 0}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Active Jobs</div>
          <div className="mt-2 text-3xl font-bold text-slate-900">{data?.active_jobs || 0}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Month Cost</div>
          <div className="mt-2 text-3xl font-bold text-slate-900">£{(data?.month_cost || 0).toLocaleString()}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Completed This Month</div>
          <div className="mt-2 text-3xl font-bold text-green-600">{data?.completed_this_month || 0}</div>
        </div>
      </div>

      {data?.by_status && (
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-700">Jobs by Status</h3>
          </div>
          <div className="px-4 py-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(data.by_status).map(([status, count]) => (
                <div key={status} className="flex items-center gap-2">
                  <div className={`px-3 py-1 rounded text-xs font-medium ${statusConfig[status as keyof typeof statusConfig]?.bg || 'bg-gray-100'} ${statusConfig[status as keyof typeof statusConfig]?.text || 'text-gray-800'}`}>
                    {statusConfig[status as keyof typeof statusConfig]?.label || status}
                  </div>
                  <span className="text-lg font-bold text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function JobsTab() {
  const [jobs, setJobs] = useState<WorkshopJob[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [showNewJobModal, setShowNewJobModal] = useState(false)
  const [newJobData, setNewJobData] = useState({ title: '', vehicle_id: 0, priority: 'routine', job_type: '' })
  const [statusChanges, setStatusChanges] = useState<Record<number, string>>({})

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true)
        const result = await api.getWorkshopJobs({})
        setJobs(result)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load jobs')
      } finally {
        setLoading(false)
      }
    }
    fetchJobs()
  }, [])

  const jobsByStatus = useMemo(() => {
    const grouped: Record<string, WorkshopJob[]> = {}
    jobs.forEach(job => {
      const key = job.status
      if (!grouped[key]) grouped[key] = []
      grouped[key].push(job)
    })
    return grouped
  }, [jobs])

  const handleStatusChange = async (jobId: number, newStatus: string) => {
    try {
      setStatusChanges(prev => ({ ...prev, [jobId]: newStatus }))
      await api.updateWorkshopJob(jobId, { status: newStatus })
      setJobs(prev =>
        prev.map(j => (j.id === jobId ? { ...j, status: newStatus as any } : j))
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status')
      setStatusChanges(prev => {
        const copy = { ...prev }
        delete copy[jobId]
        return copy
      })
    }
  }

  const handleCreateJob = async () => {
    try {
      const created = await api.createWorkshopJob(newJobData)
      setJobs(prev => [created, ...prev])
      setShowNewJobModal(false)
      setNewJobData({ title: '', vehicle_id: 0, priority: 'routine', job_type: '' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create job')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-sm text-gray-500">Loading jobs...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-sm text-red-800">
        {error}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => setShowNewJobModal(true)}
          className="inline-flex items-center px-3 py-2 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 transition-colors"
        >
          + New Job
        </button>
      </div>

      {Object.keys(statusConfig).map(status => {
        const statusJobs = jobsByStatus[status] || []
        if (statusJobs.length === 0) return null
        return (
          <div key={status} className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-700 px-1">
              {statusConfig[status as keyof typeof statusConfig].label} ({statusJobs.length})
            </div>
            <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Vehicle</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Type</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Priority</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Title</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Workshop</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Scheduled</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Assigned To</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {statusJobs.map(job => (
                    <tr
                      key={job.id}
                      onClick={() => setExpandedId(expandedId === job.id ? null : job.id)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">{job.vehicle_reg}</td>
                      <td className="px-4 py-3 text-gray-600">{job.job_type}</td>
                      <td className="px-4 py-3">
                        <PriorityBadge priority={job.priority} />
                      </td>
                      <td className="px-4 py-3 text-gray-900">{job.title}</td>
                      <td className="px-4 py-3 text-gray-600">{job.workshop_provider_name || '-'}</td>
                      <td className="px-4 py-3 text-gray-600">{job.scheduled_date || '-'}</td>
                      <td className="px-4 py-3 text-gray-600">{job.assigned_to_name || '-'}</td>
                      <td className="px-4 py-3">
                        <select
                          value={statusChanges[job.id] || job.status}
                          onChange={e => {
                            e.stopPropagation()
                            handleStatusChange(job.id, e.target.value)
                          }}
                          className="px-2 py-1 rounded bg-gray-100 text-xs font-medium border border-gray-300 cursor-pointer hover:bg-gray-200"
                        >
                          {Object.entries(statusConfig).map(([key, val]) => (
                            <option key={key} value={key}>
                              {val.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {expandedId && jobsByStatus[status]?.find(j => j.id === expandedId) && (
              <JobDetailsPanel job={jobsByStatus[status].find(j => j.id === expandedId)!} />
            )}
          </div>
        )
      })}

      {jobs.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
          <div className="text-sm text-gray-500">No workshop jobs</div>
        </div>
      )}

      {showNewJobModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-900 mb-4">New Workshop Job</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Vehicle ID</label>
                <input
                  type="number"
                  value={newJobData.vehicle_id || ''}
                  onChange={e => setNewJobData({ ...newJobData, vehicle_id: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  placeholder="e.g., 1"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Job Type</label>
                <input
                  type="text"
                  value={newJobData.job_type}
                  onChange={e => setNewJobData({ ...newJobData, job_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  placeholder="e.g., repair"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={newJobData.title}
                  onChange={e => setNewJobData({ ...newJobData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  placeholder="Job title"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={newJobData.priority}
                  onChange={e => setNewJobData({ ...newJobData, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                >
                  <option value="routine">Routine</option>
                  <option value="urgent">Urgent</option>
                  <option value="safety_critical">Safety Critical</option>
                  <option value="vor">VOR</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setShowNewJobModal(false)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateJob}
                disabled={!newJobData.title || !newJobData.vehicle_id}
                className="flex-1 px-3 py-2 bg-slate-900 text-white rounded text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function JobDetailsPanel({ job }: { job: WorkshopJob }) {
  return (
    <div className="rounded-lg bg-gray-50 p-4 border border-gray-200">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">Parts Required</div>
          {job.parts && job.parts.length > 0 ? (
            <ul className="space-y-1">
              {job.parts.map(part => (
                <li key={part.id} className="text-xs text-gray-700">
                  {part.description} (qty: {part.quantity}) — £{(part.total_cost || 0).toFixed(2)}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-xs text-gray-500">No parts recorded</div>
          )}
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">History</div>
          {job.history && job.history.length > 0 ? (
            <ul className="space-y-1">
              {job.history.slice(0, 3).map((h, i) => (
                <li key={i} className="text-xs text-gray-700">
                  <span className="text-gray-500">{h.created_at || 'N/A'}</span> — {h.new_status || h.notes || 'Updated'}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-xs text-gray-500">No history</div>
          )}
        </div>
      </div>
      {job.notes && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">Notes</div>
          <div className="text-xs text-gray-700 italic">{job.notes}</div>
        </div>
      )}
    </div>
  )
}

function ProvidersTab() {
  const [providers, setProviders] = useState<WorkshopProvider[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [newProvider, setNewProvider] = useState({
    name: '',
    contact_name: '',
    phone: '',
    email: '',
    specialisations: '',
    hourly_rate: 0,
  })

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setLoading(true)
        const result = await api.getWorkshopProviders()
        setProviders(result)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load providers')
      } finally {
        setLoading(false)
      }
    }
    fetchProviders()
  }, [])

  const handleAddProvider = async () => {
    try {
      const created = await api.createWorkshopProvider({
        ...newProvider,
        specialisations: newProvider.specialisations.split(',').map(s => s.trim()),
      })
      setProviders(prev => [...prev, created])
      setShowAddModal(false)
      setNewProvider({
        name: '',
        contact_name: '',
        phone: '',
        email: '',
        specialisations: '',
        hourly_rate: 0,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create provider')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-sm text-gray-500">Loading providers...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-sm text-red-800">
        {error}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-3 py-2 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 transition-colors"
        >
          + Add Provider
        </button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        {providers.length > 0 ? (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-4 py-2 text-left font-semibold text-gray-700">Name</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">Contact</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">Phone</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">Email</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">Specialisations</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-700">Hourly Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {providers.map(provider => (
                <tr key={provider.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{provider.name}</td>
                  <td className="px-4 py-3 text-gray-600">{provider.contact_name || '-'}</td>
                  <td className="px-4 py-3 text-gray-600">{provider.phone || '-'}</td>
                  <td className="px-4 py-3 text-gray-600">{provider.email || '-'}</td>
                  <td className="px-4 py-3 text-gray-600">
                    <div className="flex flex-wrap gap-1">
                      {provider.specialisations && provider.specialisations.map((spec, i) => (
                        <span key={i} className="inline-block bg-slate-100 text-slate-800 px-2 py-1 rounded">
                          {spec}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900 font-medium">£{provider.hourly_rate.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="px-4 py-8 text-center text-sm text-gray-500">No providers added</div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-screen overflow-y-auto">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-900 mb-4">Add Workshop Provider</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={newProvider.name}
                  onChange={e => setNewProvider({ ...newProvider, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  placeholder="Provider name"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Contact Person</label>
                <input
                  type="text"
                  value={newProvider.contact_name}
                  onChange={e => setNewProvider({ ...newProvider, contact_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  placeholder="Contact name"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={newProvider.phone}
                  onChange={e => setNewProvider({ ...newProvider, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  placeholder="+44 1234 567890"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={newProvider.email}
                  onChange={e => setNewProvider({ ...newProvider, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  placeholder="contact@provider.com"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Specialisations (comma-separated)</label>
                <input
                  type="text"
                  value={newProvider.specialisations}
                  onChange={e => setNewProvider({ ...newProvider, specialisations: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  placeholder="Engine repair, Brakes, Electrics"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Hourly Rate (£)</label>
                <input
                  type="number"
                  value={newProvider.hourly_rate}
                  onChange={e => setNewProvider({ ...newProvider, hourly_rate: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddProvider}
                disabled={!newProvider.name || !newProvider.contact_name || !newProvider.email}
                className="flex-1 px-3 py-2 bg-slate-900 text-white rounded text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export function WorkshopPage() {
  const [tab, setTab] = useState<Tab>('dashboard')

  return (
    <PortalLayout currentPage="workshop">
      <div className="p-4 lg:p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-gray-900">Workshop / VMS</h1>
        </div>

        <div className="flex gap-1 border-b border-gray-200">
          {TABS.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide border-b-2 transition-colors ${
                tab === t
                  ? 'border-slate-700 text-slate-900'
                  : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
            >
              {t.replace(/_/g, ' ')}
            </button>
          ))}
        </div>

        <div className="min-h-[400px]">
          {tab === 'dashboard' && <DashboardTab />}
          {tab === 'jobs' && <JobsTab />}
          {tab === 'providers' && <ProvidersTab />}
        </div>
      </div>
    </PortalLayout>
  )
}
